import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={className} {...props} />;
}

/** Which ends of a scrolling tab list have tabs out of view. */
const useHiddenTabs = (list: HTMLDivElement | null) => {
  const [hidden, setHidden] = React.useState({ start: false, end: false });

  React.useEffect(() => {
    if (!list) return;
    const update = () => {
      const start = list.scrollLeft > 1;
      const end = list.scrollLeft + list.clientWidth < list.scrollWidth - 1;
      setHidden((current) =>
        current.start === start && current.end === end ? current : { start, end }
      );
    };
    update();
    list.addEventListener('scroll', update, { passive: true });
    // The list or its tabs resizing (window, translations, tabs shown later) changes what fits.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(list);
    Array.from(list.children).forEach((child) => observer?.observe(child));
    return () => {
      list.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [list]);

  return hidden;
};

/** Scrolls the list (only it, never the page) so the selected tab shows in full: on load and on every
 * change, as a tab picked while cut off at an edge would stay half hidden. */
const useSelectedTabInView = (list: HTMLDivElement | null) => {
  React.useEffect(() => {
    if (!list) return;
    const reveal = (behavior: ScrollBehavior) => {
      const tab = list.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
      // A hidden list (an inactive panel, a test DOM) has no width to scroll in.
      if (!tab || !list.clientWidth) return;
      // Room for the list's padding, so the tab doesn't end flush against the edge. The list is
      // `relative`, so a tab's offsetLeft is from the list's start.
      const padding = 4;
      const left = tab.offsetLeft - padding;
      const right = left + tab.offsetWidth + padding * 2;
      if (left < list.scrollLeft) list.scrollTo({ left, behavior });
      else if (right > list.scrollLeft + list.clientWidth)
        list.scrollTo({ left: right - list.clientWidth, behavior });
    };
    reveal('auto');
    const observer = new MutationObserver(() => reveal('smooth'));
    observer.observe(list, { subtree: true, attributeFilter: ['data-state'] });
    return () => observer.disconnect();
  }, [list]);
};

/**
 * An arrow beside the list, shown while its tabs don't all fit (a phone, many tabs): it scrolls the list
 * that way, and is faded when there is nothing more on its side. Keyboard users move with the tab
 * list's own arrow keys, so it is left out of the tab order and the accessibility tree.
 */
const ScrollArrow = ({
  side,
  isActive,
  onClick,
}: {
  side: 'start' | 'end';
  isActive: boolean;
  onClick: () => void;
}) => {
  const Icon = side === 'start' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden="true"
      disabled={!isActive}
      onClick={onClick}
      className="flex size-7 shrink-0 items-center justify-center rounded-md text-foreground transition-colors duration-fast ease-standard hover:bg-muted disabled:cursor-not-allowed disabled:text-disabled-foreground disabled:hover:bg-transparent"
    >
      <Icon className="size-4" strokeWidth={2.5} />
    </button>
  );
};

function TabsList({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const [list, setList] = React.useState<HTMLDivElement | null>(null);
  const hidden = useHiddenTabs(list);
  useSelectedTabInView(list);
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      setList(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );
  const scrollBy = (direction: 1 | -1) =>
    list?.scrollBy({ left: direction * list.clientWidth * 0.6, behavior: 'smooth' });
  const overflows = hidden.start || hidden.end;

  return (
    // The arrows sit outside the list, on its line, so they never cover a tab.
    <div className="flex min-w-0 max-w-full items-center gap-1">
      {overflows && (
        <ScrollArrow side="start" isActive={hidden.start} onClick={() => scrollBy(-1)} />
      )}
      <TabsPrimitive.List
        ref={setRefs}
        data-slot="tabs-list"
        className={cn(
          // HR Module Prototype `.tabs`: one segmented row on the control track, 36px high with a 3px inset
          // and 4px between triggers.
          'relative inline-flex h-9 w-fit min-w-0 max-w-full items-center justify-start gap-1 overflow-x-auto overflow-y-hidden rounded-md bg-control-track p-[3px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className
        )}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
      {overflows && <ScrollArrow side="end" isActive={hidden.end} onClick={() => scrollBy(1)} />}
    </div>
  );
}

/**
 * One tab. `count` is the number of items waiting in it (Component Map: "Tabs with waiting work show a
 * count"), shown as an outline badge after the label; nothing shows for 0 or while it is unknown.
 */
function TabsTrigger({
  className,
  children,
  count,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & { count?: number | null }) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex h-full shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-sm border border-transparent px-2.5 text-sm font-medium text-tab-foreground transition-[color,background-color,box-shadow] duration-normal ease-standard hover:bg-card/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:!text-disabled-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow data-[state=active]:hover:bg-card dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:data-[state=active]:hover:bg-input/30[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      {count ? (
        <span className="ml-0.5 inline-flex h-5 items-center rounded-sm border px-1.5 text-xs font-medium tabular-nums text-foreground">
          {count}
        </span>
      ) : null}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        'mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className
      )}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
